import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wrench, ClipboardCheck, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

function formatFecha(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
  return `${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
}

const ESTADO_STYLE = {
  OK:  { bg: '#12382D', color: '#4CAF50' },
  RO:  { bg: '#1C1200', color: '#F59E0B' },
  RAI: { bg: '#2D0F0F', color: '#EF4444' },
  NA:  { bg: '#1A1F1D', color: '#8A9994' },
};

function Section({ title, children }) {
  return (
    <div style={{
      background: '#111714', marginTop: 10, padding: '14px 16px',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#4CAF50',
        textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, valueStyle }) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', padding: '7px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <span style={{ fontSize: 13, color: '#8A9994', flex: 1 }}>{label}</span>
      <span style={{
        fontSize: 13, color: '#F4F7F5', fontWeight: 600,
        flex: 2, textAlign: 'right', ...valueStyle,
      }}>
        {value}
      </span>
    </div>
  );
}

export default function FormDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    api.get(`/formularios/${id}`)
      .then(r => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1>Cargando...</h1>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <div className="spinner" />
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1>Error</h1>
      </div>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#EF4444', fontSize: 14 }}>
          No se pudo cargar el comprobante.
        </p>
      </div>
    </div>
  );

  const esMantenimiento = data.tipo === 'mantenimiento';
  const tieneRAI = (data.checklist || []).some(i => i.estado === 'RAI');

  const secciones = {};
  (data.checklist || []).forEach(item => {
    if (!secciones[item.seccion]) secciones[item.seccion] = [];
    secciones[item.seccion].push(item);
  });

  const colorEstado = (e) => {
    if (!e) return '#8A9994';
    const el = e.toLowerCase();
    if (el.includes('fuera') || el.includes('detenido')) return '#EF4444';
    if (el.includes('observaci')) return '#F59E0B';
    return '#4CAF50';
  };

  return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1 style={{ flex: 1, margin: 0 }}>{data.folio}</h1>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: esMantenimiento ? '#12382D' : '#0D1E2E',
          borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700,
          color: esMantenimiento ? '#4CAF50' : '#60A5FA', whiteSpace: 'nowrap',
        }}>
          {esMantenimiento
            ? <Wrench size={12} strokeWidth={2} />
            : <ClipboardCheck size={12} strokeWidth={2} />
          }
          {esMantenimiento ? 'Mant.' : 'Visita'}
        </span>
      </div>

      <div style={{ padding: 0 }}>
        {/* Banner RAI */}
        {tieneRAI && (
          <div style={{
            background: '#2D0F0F', padding: '10px 16px',
            borderBottom: '1px solid #3D1515', textAlign: 'center',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#EF4444', fontWeight: 700, fontSize: 13 }}>
              <AlertTriangle size={14} strokeWidth={2.5} />
              Este formulario contiene ítems RAI
            </span>
          </div>
        )}

        {/* Ascensor */}
        <div style={{ background: '#0f6e56', padding: '14px 16px' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'white', marginBottom: 4 }}>
            {data.label}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginBottom: 2 }}>
            {data.comunidad} · N° {data.n_ascensor} · Torre {data.torre}
          </div>
          {data.direccion && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
              {data.direccion}
            </div>
          )}
        </div>

        {/* Datos generales */}
        <Section title="Datos generales">
          <Row label="Fecha"        value={formatFecha(data.fecha_visita)} />
          <Row label="Técnico"      value={data.tecnico_nombre} />
          <Row label="Cliente"      value={data.nombre_cliente} />
          <Row label="Ingreso"      value={data.hora_ingreso} />
          <Row label="Salida"       value={data.hora_salida} />
          <Row label="Tipo visita"  value={data.tipo_visita} />
          {data.presupuesto_numero && (
            <Row label="Presupuesto"
              value={data.presupuesto_numero + (data.presupuesto_nombre ? ` — ${data.presupuesto_nombre}` : '')} />
          )}
          {data.estado_final_equipo && (
            <Row
              label="Estado equipo"
              value={data.estado_final_equipo}
              valueStyle={{ color: colorEstado(data.estado_final_equipo), fontWeight: 700 }}
            />
          )}
        </Section>

        {/* Resumen / Observaciones */}
        {(data.resumen_visita || data.observaciones_generales) && (
          <Section title={esMantenimiento ? 'Observaciones generales' : 'Resumen de la visita'}>
            <p style={{ margin: 0, fontSize: 13, color: '#C9D3CE', lineHeight: 1.7 }}>
              {data.resumen_visita || data.observaciones_generales}
            </p>
          </Section>
        )}

        {/* Checklist */}
        {esMantenimiento && Object.keys(secciones).length > 0 && (
          <Section title="Checklist">
            {Object.entries(secciones).map(([seccion, items]) => (
              <div key={seccion} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: '#6B7C74',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  marginBottom: 6,
                }}>
                  {seccion}
                </div>
                {items.map((item, i) => {
                  const est = ESTADO_STYLE[item.estado] || ESTADO_STYLE.NA;
                  return (
                    <div key={i}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '5px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}>
                        <span style={{ fontSize: 12, color: '#C9D3CE', flex: 1, marginRight: 8 }}>
                          {item.descripcion}
                        </span>
                        <span style={{
                          background: est.bg, color: est.color,
                          borderRadius: 6, padding: '2px 10px',
                          fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
                        }}>
                          {item.estado || 'N/A'}
                        </span>
                      </div>
                      {item.observacion && (
                        <div style={{
                          fontSize: 11, color: '#F59E0B', fontStyle: 'italic',
                          paddingLeft: 8, borderLeft: '2px solid #F59E0B',
                          margin: '2px 0 4px 0',
                        }}>
                          {item.observacion}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </Section>
        )}

        {/* Firmas */}
        {(data.firma_tecnico || data.firma_cliente) && (
          <Section title="Firmas">
            <div style={{ display: 'flex', gap: 16 }}>
              {data.firma_tecnico && (
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <img src={data.firma_tecnico} alt="Firma técnico"
                    style={{
                      width: '100%', maxHeight: 110, objectFit: 'contain',
                      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                      background: '#0D1311',
                    }} />
                  <div style={{ fontSize: 11, color: '#8A9994', marginTop: 4 }}>Técnico</div>
                </div>
              )}
              {data.firma_cliente && (
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <img src={data.firma_cliente} alt="Firma cliente"
                    style={{
                      width: '100%', maxHeight: 110, objectFit: 'contain',
                      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                      background: '#0D1311',
                    }} />
                  <div style={{ fontSize: 11, color: '#8A9994', marginTop: 4 }}>Cliente</div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Fotos */}
        {data.fotos && data.fotos.length > 0 && (
          <Section title={`Fotografías (${data.fotos.length})`}>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
              {data.fotos.map((foto, i) => (
                <img key={i} src={foto.url || foto.dataURL} alt={`Foto ${i + 1}`}
                  style={{
                    width: 160, height: 120, objectFit: 'cover',
                    borderRadius: 10, flexShrink: 0,
                  }} />
              ))}
            </div>
          </Section>
        )}

        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}
